<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { legalActivitiesAPI } from 'dashboard/api/legalActivities';
import ScheduleActivityModal from 'dashboard/components/widgets/conversation/ScheduleActivityModal.vue';
import { dataHoraEscritorio } from 'dashboard/helper/fusoEscritorio';
import { tokensDoTipo } from 'dashboard/routes/dashboard/appointments/coresDeAtividade';
import EsqueletoLista from 'dashboard/components-next/EsqueletoLista.vue';

// Compromissos do cliente na barra lateral da conversa (pedido do Victor,
// 02/09/2026: "quero que no card do usuário apareça esse agendamento, tipo
// acima de Financeiro do cliente").
//
// Fecha o ciclo do botão "Agendar atendimento": marcou na conversa, aparece
// aqui. Sem isto, quem agendou não tem como confirmar que deu certo sem abrir
// a agenda — e quem atende depois não sabe que já existe compromisso marcado.
//
// 🔴 EFICIÊNCIA: esta barra abre em TODA conversa, o dia inteiro.
//   - `limit` e `from` cortam no banco: só o que ainda vai acontecer, no
//     máximo 5. Sem isso viriam as 4.539 atividades da conta.
//   - falha em silêncio: o atendimento não pode quebrar porque a agenda está
//     fora do ar. A seção simplesmente não aparece.

const props = defineProps({
  contactId: { type: [Number, String], default: null },
  // Necessários para editar sem sair da conversa: o modal precisa saber em qual
  // conversa está e de quem é o compromisso.
  conversationId: { type: [Number, String], default: null },
  contact: { type: Object, default: () => ({}) },
});

const emEdicao = ref(null);

const { t } = useI18n();
const router = useRouter();
const accountId = useMapGetter('getCurrentAccountId');

const compromissos = ref([]);
const carregando = ref(false);

const buscar = async id => {
  if (!id) {
    compromissos.value = [];
    return;
  }
  carregando.value = true;
  try {
    const { data } = await legalActivitiesAPI.list({
      contact_id: id,
      from: new Date().toISOString(),
      limit: 5,
    });
    // A conversa pode ter trocado de contato enquanto a agenda respondia.
    if (id !== props.contactId) return;
    compromissos.value = data?.payload || [];
  } catch {
    if (id === props.contactId) compromissos.value = [];
  } finally {
    if (id === props.contactId) carregando.value = false;
  }
};

watch(() => props.contactId, buscar, { immediate: true });

// O modal que cria o compromisso vive no header da conversa, não aqui dentro.
// São componentes irmãos: sem este aviso, a seção só mostrava o compromisso
// depois de um F5 (visto pelo Victor, 02/09).
const recarregar = () => buscar(props.contactId);
onMounted(() => emitter.on(BUS_EVENTS.SCHEDULED_ACTIVITY_SAVED, recarregar));
onUnmounted(() => emitter.off(BUS_EVENTS.SCHEDULED_ACTIVITY_SAVED, recarregar));

// Fuso do ESCRITÓRIO, não do navegador: é hora de compromisso, e uma
// audiência mostrada uma hora antes é prazo perdido.
const diaDe = iso => dataHoraEscritorio(iso, { day: '2-digit' }) || '—';
const mesDe = iso =>
  (dataHoraEscritorio(iso, { month: 'short' }) || '').replace('.', '');
const horaDe = iso =>
  dataHoraEscritorio(iso, { hour: '2-digit', minute: '2-digit' }) || '';
const quandoDe = iso =>
  [dataHoraEscritorio(iso, { weekday: 'long' }), horaDe(iso)]
    .filter(Boolean)
    .join(' · ');

// O ladrilho da data leva a cor do TIPO, a mesma da agenda: compromisso azul,
// tarefa íris.
const estiloDoTipo = c => {
  const tom = tokensDoTipo(c.activity_type);
  return { '--cua-fundo': tom.bg, '--cua-tinta': tom.text };
};

// A rota é `appointments/agenda` — a agenda jurídica mora sob esse prefixo,
// apesar do nome (ver appointments.routes.js: name 'appointments_agenda',
// componente AgendaActivitiesList). Na primeira versão eu apontei para
// `legal/agenda`, que NÃO existe: o clique saía da conversa e não chegava a
// lugar nenhum (visto pelo Victor em 02/09).
//
// `?atividade=<id>` abre o compromisso já selecionado — a tela da agenda lê
// essa query no onMounted e limpa depois, então F5 não reabre o modal.
const abrirAgenda = id => {
  router.push({
    name: 'appointments_agenda',
    params: { accountId: accountId.value },
    query: id ? { atividade: id } : {},
  });
};
</script>

<template>
  <section v-if="carregando || compromissos.length" class="cua">
    <p class="advup-rotulo advup-rotulo--secao cua__rot">
      {{ t('CONTACT_PANEL.UPCOMING_ACTIVITIES.TITLE') }}
    </p>

    <EsqueletoLista
      v-if="carregando"
      :linhas="2"
      :altura="52"
      :rotulo="t('CONTACT_PANEL.UPCOMING_ACTIVITIES.LOADING')"
    />

    <template v-else>
      <ul class="cua__lista">
        <li v-for="(c, i) in compromissos" :key="c.id">
          <button
            type="button"
            class="advup-card cua__item advup-entra"
            :style="[{ '--i': i }, estiloDoTipo(c)]"
            @click="emEdicao = c"
          >
            <span class="cua__data" aria-hidden="true">
              <span class="cua__dia">{{ diaDe(c.start_at) }}</span>
              <span class="cua__mes">{{ mesDe(c.start_at) }}</span>
            </span>
            <span class="cua__txt">
              <span class="cua__desc">{{ c.description }}</span>
              <span class="cua__quando">
                {{ quandoDe(c.start_at) }}
              </span>
            </span>
            <i class="i-lucide-pencil cua__editar" aria-hidden="true" />
          </button>
        </li>
      </ul>

      <button type="button" class="cua__agenda" @click="abrirAgenda()">
        {{ t('CONTACT_PANEL.UPCOMING_ACTIVITIES.OPEN_AGENDA') }}
        <i class="i-lucide-arrow-right" aria-hidden="true" />
      </button>
    </template>

    <!-- Editar ali mesmo (pedido do Victor, 02/09). Mandar para a agenda só
         para corrigir um horário quebraria o propósito da ferramenta. -->
    <Transition name="advup-saida">
      <ScheduleActivityModal
        v-if="emEdicao"
        :conversation-id="conversationId"
        :contact="contact"
        :activity="emEdicao"
        @close="emEdicao = null"
        @created="buscar(props.contactId)"
      />
    </Transition>
  </section>
</template>

<style scoped>
.cua {
  display: flex;
  flex-direction: column;
  gap: var(--esp-2);
}

.cua__rot {
  margin: 0;
  padding: 0 var(--esp-1);
}

.cua__lista {
  display: flex;
  flex-direction: column;
  gap: var(--esp-2);
  margin: 0;
  padding: 0;
  list-style: none;
}

.cua__item {
  display: flex;
  align-items: center;
  gap: var(--esp-3);
  width: 100%;
  padding: var(--esp-2);
  text-align: left;
  cursor: pointer;
  transition:
    translate var(--mov-2) var(--mov-curva-pulo),
    box-shadow var(--mov-2) var(--mov-curva);
}

.cua__item:hover {
  translate: 2px 0;
  box-shadow: 0 6px 16px -10px rgb(15 23 42 / 30%);
}

.cua__item:focus-visible {
  outline: 2px solid rgb(var(--blue-9));
  outline-offset: 2px;
}

/* A folhinha do calendário: o dia grande e o mês embaixo, no tom do tipo. */
.cua__data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: var(--r-3);
  background: var(--cua-fundo);
  color: var(--cua-tinta);
}

.cua__dia {
  font-size: var(--txt-dado);
  font-weight: var(--peso-titulo);
  line-height: var(--linha-apertada);
  font-variant-numeric: tabular-nums;
}

.cua__mes {
  font-size: var(--txt-rotulo);
  font-weight: var(--peso-rotulo);
  letter-spacing: var(--rastro-rotulo);
  text-transform: uppercase;
}

.cua__txt {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
}

.cua__quando {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Até duas linhas: o assunto é o que a pessoa precisa ler. */
.cua__desc {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-height: var(--linha-normal);
  font-size: var(--txt-corpo);
  font-weight: var(--peso-dado);
  color: rgb(var(--slate-12));
}

.cua__quando {
  font-size: var(--txt-nota);
  color: rgb(var(--slate-11));
}

.cua__editar {
  flex-shrink: 0;
  font-size: var(--ico-1);
  color: rgb(var(--slate-9));
  opacity: 0;
  transition: opacity var(--mov-1) var(--mov-curva);
}

.cua__item:hover .cua__editar,
.cua__item:focus-visible .cua__editar {
  opacity: 1;
}

@media (hover: none) {
  .cua__editar {
    opacity: 1;
  }
}

.cua__agenda {
  display: inline-flex;
  align-items: center;
  align-self: flex-end;
  gap: var(--esp-1);
  padding: 0;
  border: 0;
  background: none;
  font-size: var(--txt-nota);
  font-weight: var(--peso-forte);
  color: rgb(var(--blue-11));
  cursor: pointer;
}

.cua__agenda > i {
  font-size: var(--ico-1);
  transition: translate var(--mov-2) var(--mov-curva-pulo);
}

.cua__agenda:hover > i {
  translate: 2px 0;
}

@media (prefers-reduced-motion: reduce) {
  .cua__item,
  .cua__agenda > i {
    transition: none;
  }

  .cua__item:hover {
    translate: none;
  }
}
</style>
